import { AppState as A0AppState, Auth0Provider as A0Provider } from '@auth0/auth0-react';
import omit from 'lodash/omit';
import { useRouter } from 'next/navigation';
import querystring from 'query-string';
import { FC, ReactNode, useCallback } from 'react';

import { EnvConfig } from '@/env_config';
import type { DynamicRoute } from '@/types/next';
import { protectOpenRedirect } from '@/utils/protectOpenRedirect';

interface Props {
	children: ReactNode;
}

const getRedirectUri = (): string => {
	// In dev environments, take the user back to where they started.
	// This will allow E2E tests which intentionally avoid the CDN domain to stay off of the CDN domain
	if (process.env.LOG_ENV === 'dev' && typeof window !== 'undefined' && window.location.origin) {
		return window.location.origin;
	}
	return EnvConfig.partnerPortalUrl;
};

export default function Auth0Provider({ children }: Props): ReturnType<FC> {
	const router = useRouter();

	const onRedirectCallback = useCallback(
		(appState?: A0AppState | undefined): void => {
			const query = appState?.query ?? {};
			const returnTo = query?.returnTo ?? '/';
			const newQuery = omit(query, ['returnTo', 'code', 'state']);
			const pathname = protectOpenRedirect(decodeURIComponent(String(returnTo)));
			const search = querystring.stringify(newQuery);
			const url = search ? `${pathname}?${search}` : pathname;
			// Use Next.js router instead of window.location.assign to avoid full page reload
			router.push(url as DynamicRoute);
		},
		[router],
	);

	return (
		<A0Provider
			domain={EnvConfig.auth0Domain}
			clientId={EnvConfig.auth0ClientId}
			authorizationParams={{
				redirect_uri: `${getRedirectUri()}/`,
				'x-device-platform': 'partner-portal',
				'x-device-version': process.env.BUILD_ID || 'unknown',
			}}
			cacheLocation="localstorage"
			useRefreshTokens={true}
			onRedirectCallback={onRedirectCallback}
		>
			{children}
		</A0Provider>
	);
}
